
import React, { useState, useEffect } from 'react';
import { X, Save, MessageSquare } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface AddMockExamModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave?: () => void;
    selectedStudentId?: string;
    initialData?: any;
}

const AddMockExamModal: React.FC<AddMockExamModalProps> = ({ isOpen, onClose, onSave, selectedStudentId, initialData }) => {
    const { user } = useAuth();
    const [students, setStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [sendSms, setSendSms] = useState(false);

    const [formData, setFormData] = useState({
        studentId: '',
        examDate: new Date().toISOString().split('T')[0],
        examType: 'SAT',
        scoreTotal: '',
        notes: ''
    });

    const [scoreDetails, setScoreDetails] = useState<Record<string, number>>({});

    useEffect(() => {
        if (isOpen) {
            fetchStudents();
            if (initialData) {
                setFormData({
                    studentId: initialData.student_id,
                    examDate: initialData.exam_date,
                    examType: initialData.exam_type,
                    scoreTotal: initialData.score_total?.toString() || '',
                    notes: initialData.notes || ''
                });
                setScoreDetails(initialData.score_details || {});
            } else if (selectedStudentId) {
                setFormData(prev => ({ ...prev, studentId: selectedStudentId }));
            }
        }
    }, [isOpen, selectedStudentId, initialData]);

    const fetchStudents = async () => {
        const { data } = await supabase
            .from('testprep_students')
            .select('id, first_name, last_name, phone, parent_name, parent_phone')
            .order('first_name');
        if (data) {
            setStudents(data.map((s: any) => ({
                id: s.id,
                name: `${s.first_name} ${s.last_name}`,
                phone: s.phone,
                parent_name: s.parent_name,
                parent_phone: s.parent_phone
            })));
        }
    };

    // Exam type specific sections with max scores
    const examConfigs: Record<string, { sections: string[], maxScores: Record<string, number>, autoCalc: boolean, maxTotal?: number }> = {
        'SAT': {
            sections: ['Math', 'Reading & Writing'],
            maxScores: { 'Math': 800, 'Reading & Writing': 800 },
            autoCalc: true,
            maxTotal: 1600
        },
        'IELTS': {
            sections: ['Listening', 'Reading', 'Writing', 'Speaking'],
            maxScores: { 'Listening': 9, 'Reading': 9, 'Writing': 9, 'Speaking': 9 },
            autoCalc: false,
            maxTotal: 9
        },
        'TOEFL': {
            sections: ['Reading', 'Listening', 'Speaking', 'Writing'],
            maxScores: { 'Reading': 30, 'Listening': 30, 'Speaking': 30, 'Writing': 30 },
            autoCalc: true,
            maxTotal: 120
        },
        'IMAT': { sections: ['Critical Thinking', 'Biology', 'Chemistry', 'Math & Physics'], maxScores: {}, autoCalc: true, maxTotal: 90 },
        'TIL-I': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'TEST-ARCH': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'TIL-A': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'TOLC-I': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-SU': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-PSI': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-F': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-E': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 36 },
        'TOLC-AV': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-S': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 55 },
        'BOCCONI TEST': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLD-D': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'TIL-D': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'TOLC-B': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'TOLC-LP': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 30 },
        'TOLC-SPS': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 40 },
        'CENT-S': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 55 },
        'ITA BOCCONI TEST': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 50 },
        'IYA TIL-I': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'ITA TIL-I': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'ITA TOL-D': { sections: [], maxScores: {}, autoCalc: true, maxTotal: 100 },
        'CILS': { sections: [], maxScores: {}, autoCalc: true },
        'CELI': { sections: [], maxScores: {}, autoCalc: true }
    };

    const getSections = (type: string) => examConfigs[type]?.sections || [];

    // Calculate total score based on exam type
    const calculateTotalScore = (type: string, details: Record<string, number>): number => {
        const config = examConfigs[type];
        if (!config) return 0;

        const values = Object.values(details);
        if (values.length === 0) return 0;

        if (type === 'IELTS') {
            // IELTS uses average
            const sum = values.reduce((a, b) => a + b, 0);
            return parseFloat((sum / values.length).toFixed(1));
        } else {
            // Most exams use sum
            return values.reduce((a, b) => a + b, 0);
        }
    };

    const handleSectionScoreChange = (section: string, value: string) => {
        const newDetails = {
            ...scoreDetails,
            [section]: parseFloat(value) || 0
        };
        setScoreDetails(newDetails);

        // Auto-calculate total score
        const config = examConfigs[formData.examType];
        if (config?.autoCalc || formData.examType === 'IELTS') {
            const total = calculateTotalScore(formData.examType, newDetails);
            setFormData(prev => ({ ...prev, scoreTotal: total.toString() }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                student_id: formData.studentId,
                exam_date: formData.examDate,
                exam_type: formData.examType,
                score_total: parseFloat(formData.scoreTotal),
                score_details: scoreDetails,
                notes: formData.notes
            };

            if (initialData?.id) {
                const { error } = await supabase
                    .from('testprep_mock_exams')
                    .update(payload)
                    .eq('id', initialData.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('testprep_mock_exams')
                    .insert(payload);
                if (error) throw error;
            }

            // Send SMS if requested (only for new records usually, but I'll leave it as is for simplicity)
            if (sendSms) {
                const student = students.find(s => s.id === formData.studentId);
                if (student && student.parent_phone) {
                    try {
                        const config = examConfigs[formData.examType];
                        const maxTotal = config?.maxTotal;
                        const scoreDisplay = maxTotal ? `${formData.scoreTotal}/${maxTotal}` : formData.scoreTotal;
                        const message = `Sayın Veli, Öğrenciniz ${student.name}'in ${formData.examDate} tarihli ${formData.examType} deneme sınavı sonucu: ${scoreDisplay}. Başarılar dileriz. - Pisa Eğitim`;

                        await supabase.functions.invoke('send-sms', {
                            body: {
                                phones: [student.parent_phone.replace(/\D/g, '')],
                                message,
                                userId: user?.id,
                                branchId: user?.branchId
                            }
                        });
                    } catch (smsError) {
                        console.error('SMS sending failed:', smsError);
                    }
                }
            }

            if (onSave) onSave();
            onClose();
            alert(initialData ? 'Sınav sonucu güncellendi.' : 'Sınav sonucu başarıyla kaydedildi.');

            if (!initialData) {
                setFormData({
                    studentId: selectedStudentId || '',
                    examDate: new Date().toISOString().split('T')[0],
                    examType: 'SAT',
                    scoreTotal: '',
                    notes: ''
                });
                setScoreDetails({});
                setSendSms(false);
            }

        } catch (error: any) {
            console.error('Error saving mock exam:', error);
            alert('Hata oluştu: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{initialData ? 'Sınav Sonucunu Düzenle' : 'Yeni Sınav Sonucu Ekle'}</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">

                    {/* Student Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Öğrenci</label>
                        <select
                            required
                            disabled={!!selectedStudentId}
                            value={formData.studentId}
                            onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60"
                        >
                            <option value="">Seçiniz</option>
                            {students.map((student: any) => (
                                <option key={student.id} value={student.id}>
                                    {student.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sınav Tarihi</label>
                            <input
                                type="date"
                                required
                                value={formData.examDate}
                                onChange={(e) => setFormData({ ...formData, examDate: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sınav Türü</label>
                            <select
                                value={formData.examType}
                                onChange={(e) => {
                                    setFormData({ ...formData, examType: e.target.value });
                                    setScoreDetails({});
                                }}
                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="SAT">SAT</option>
                                <option value="IELTS">IELTS</option>
                                <option value="TOEFL">TOEFL</option>
                                <option value="IMAT">IMAT</option>
                                <option value="TIL-I">TIL-I</option>
                                <option value="TEST-ARCH">TEST-ARCH</option>
                                <option value="TIL-A">TIL-A</option>
                                <option value="TOLC-I">TOLC-I</option>
                                <option value="TOLC-SU">TOLC-SU</option>
                                <option value="TOLC-PSI">TOLC-PSI</option>
                                <option value="TOLC-F">TOLC-F</option>
                                <option value="TOLC-E">TOLC-E</option>
                                <option value="TOLC-AV">TOLC-AV</option>
                                <option value="TOLC-S">TOLC-S</option>
                                <option value="TOLC-B">TOLC-B</option>
                                <option value="TOLC-LP">TOLC-LP</option>
                                <option value="TOLC-SPS">TOLC-SPS</option>
                                <option value="BOCCONI TEST">BOCCONI TEST</option>
                                <option value="CENT-S">CENT-S</option>
                                <option value="ITA BOCCONI TEST">ITA BOCCONI TEST</option>
                                <option value="IYA TIL-I">IYA TIL-I</option>
                                <option value="ITA TIL-I">ITA TIL-I</option>
                                <option value="TOLD-D">TOLD-D</option>
                                <option value="TIL-D">TIL-D</option>
                                <option value="ITA TOL-D">ITA TOL-D</option>
                                <option value="CILS">CILS</option>
                                <option value="CELI">CELI</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Toplam Puan</label>
                        <input
                            type="number"
                            required
                            placeholder="Örn: 1450"
                            value={formData.scoreTotal}
                            onChange={(e) => setFormData({ ...formData, scoreTotal: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-lg"
                        />
                    </div>

                    {/* Dynamic Section Scores */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Bölüm Puanları</h4>
                        <div className="grid grid-cols-2 gap-3">
                            {getSections(formData.examType).map((section) => (
                                <div key={section}>
                                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{section}</label>
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={scoreDetails[section] || ''}
                                        onChange={(e) => handleSectionScoreChange(section, e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            ))}
                            {getSections(formData.examType).length === 0 && (
                                <p className="text-sm text-gray-400 col-span-2">Bu sınav türü için özel bölüm tanımlanmamış.</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                        <textarea
                            rows={3}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="Sınav ile ilgili notlar..."
                        />
                    </div>

                    <div className="flex items-center space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                        <input
                            type="checkbox"
                            id="sendSms"
                            checked={sendSms}
                            onChange={(e) => setSendSms(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <label htmlFor="sendSms" className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                            <MessageSquare className="w-4 h-4 text-blue-500" />
                            <span>Sonucu veliye SMS olarak gönder</span>
                        </label>
                    </div>


                    <div className="flex justify-end pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {loading ? 'Kaydediliyor...' : (initialData ? 'Güncelle' : 'Kaydet')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default AddMockExamModal;

